import { useState } from 'react'
import Button from 'components/ui/Button'
import TextField from 'components/ui/TextField'
import CheckBox from 'components/ui/CheckBox'
import getMutationError from 'utils/getMutationError'

import isEmail from 'utils/isEmail'
import { useEmailVerificationCodeSend } from './types/EmailVerificationCodeSend'

type Props = {
  onVerificationSent: (email: string) => void
  onChangeEmail: () => void
  verifying: boolean
  initialEmail?: string
  buttonName: string
  catering: boolean
  showTerms?: boolean
  setCanUsePasswordAuth: (canUsePasswordAuth: boolean) => void
  verified: boolean
}

export default function VerifyEmail({
  onVerificationSent,
  verifying,
  onChangeEmail,
  initialEmail = '',
  showTerms = false,
  buttonName,
  catering,
  setCanUsePasswordAuth,
  verified
}: Props) {
  const [prevEmail, setPrevEmail] = useState(initialEmail)
  const [email, setEmail] = useState(initialEmail)
  const [error, setError] = useState<string | undefined>(undefined)
  const [tosError, setTosError] = useState<boolean>(false)
  const [agreedToTos, setAgreedToTos] = useState<boolean>(false)
  const [sendCode] = useEmailVerificationCodeSend({})
  const hasNotChanged =
    initialEmail?.length > 0 && initialEmail === email?.replace(/[()\s-]/g, '')

  const onVerifyEmail = async () => {
    setCanUsePasswordAuth(false)
    if (showTerms && !agreedToTos) {
      setTosError(true)
      return
    }
    setTosError(false)

    if (prevEmail === email) {
      onVerificationSent(email)
      return
    }
    setPrevEmail(email)
    const response = await sendCode({ variables: { email } })
    const mutationError = getMutationError(response!)
    if (mutationError) {
      setError(mutationError)
    } else {
      setError(undefined)
      onVerificationSent(email)
      setCanUsePasswordAuth(
        response?.data?.emailVerificationCodeSend?.canUsePasswordAuth ?? false
      )
    }
  }

  const description = catering ? (
    <span>
      We just need to verify via Email code that you&apos;re not a robot.
    </span>
  ) : (
    <span>Requires email confirmation.</span>
  )

  const nextDisabled = !isEmail(email) || hasNotChanged || verified || verifying
  return (
    <>
      <div className="flex items-end justify-between mb-5">
        <div className="grow">
          <TextField
            description={description}
            disabled={verifying || verified}
            error={error}
            name="Email"
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(e) => {
              if (e.keyCode !== 13) {
                return
              }
              if (nextDisabled) {
                return
              }
              onVerifyEmail()
            }}
            placeholder="Email"
            value={email}
          />
        </div>

        <div className="grow mt-1 ml-4">
          <Button
            color="primary"
            disabled={nextDisabled}
            onClick={onVerifyEmail}
            size="input"
            variant="contained"
            fullWidth
          >
            {buttonName}
          </Button>
        </div>
      </div>

      {showTerms && !verifying && (
        <CheckBox
          error={tosError ? 'Required' : undefined}
          label={
            <div className="flex text-sm italic text-gray-500">
              I agree to receive SMS message updates. <br />
              We will only text you about services you have requested. <br />
              Msg & Data rates may apply. Text STOP to cancel, HELP for
              information.
            </div>
          }
          onChange={(newValue) => {
            setAgreedToTos(newValue)
            setTosError(!newValue)
          }}
          value={agreedToTos}
        />
      )}

      {(verifying || verified) && (
        <div className="-mt-4 mb-5">
          <Button
            color="default"
            onClick={() => {
              setError(undefined)
              onChangeEmail()
            }}
            size="small"
            variant="anchor"
          >
            Use another email
          </Button>
        </div>
      )}
    </>
  )
}
